/**
 * Sidebar v2 row model: every subscription and derived value both row
 * variants share, computed once per row and split into focused hooks.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (SidebarThreadRow body, ~lines 1018-1350) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted to forge's shared row surface: change
 * requests through forge's useSidebarThreadPullRequest, terminal state
 * through forge's terminalStateStore, branch display follows forge's
 * resolveDisplayedThreadBranch rule, and the status model is this port's
 * sidebarV2Status. Unsent-composer tinting and file-drop targets have no
 * forge listing data source yet (documented seams).
 */
import { useCallback, useEffect, useState } from "react";
import { scopedThreadKey, scopeThreadRef } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";

import { useGitStatus } from "~/lib/gitStatusState";
import { selectThreadTerminalState, useTerminalStateStore } from "~/terminalStateStore";
import { useUiStateStore } from "~/uiStateStore";
import { useThreadSelectionStore } from "~/threadSelectionStore";
import {
  resolveDisplayedThreadBranch,
  terminalStatusFromRunningIds,
} from "~/components/sidebar/SidebarThreadRow.helpers";
import { useSidebarThreadPullRequest } from "~/components/sidebar/SidebarThreadPullRequestBadge";
import { canSnooze } from "./sidebarV2Settled";
import {
  hasUnseenCompletion,
  resolveSidebarThreadStatus,
  shouldRecedeSidebarThread,
} from "./sidebarV2Status";
import type { SidebarV2RowProps } from "./sidebarV2RowParts";
import { useRetainedValue, useSidebarRowSubscriptionLease } from "./sidebarV2RowHooks";

/** Viewport-leased live status plus the row element ref the lease observes. */
function useRowLease(props: SidebarV2RowProps) {
  return useSidebarRowSubscriptionLease(props.isActive);
}

/** Forge terminal state for this thread's environment. */
function useRowTerminal(threadRef: ScopedThreadRef) {
  const runningTerminalIds = useTerminalStateStore(
    (state) =>
      selectThreadTerminalState(state.terminalStateByThreadKey, threadRef).runningTerminalIds,
  );
  return {
    terminalStatus: terminalStatusFromRunningIds(runningTerminalIds),
    terminalProcessCount: runningTerminalIds.length,
  };
}

/** Live git status for the row's checkout, leased to the viewport, with the
 * last value retained so a released lease never blanks the PR badge. */
function useRowGit(props: SidebarV2RowProps, leaseLiveStatus: boolean) {
  const { thread } = props;
  const gitCwd = thread.worktreePath ?? props.projectCwd;
  const gitStatusState = useGitStatus({
    environmentId: leaseLiveStatus ? thread.environmentId : null,
    cwd: leaseLiveStatus ? gitCwd : null,
  });
  const retainedGitStatus = useRetainedValue(
    `${thread.environmentId}:${gitCwd}`,
    gitStatusState.data,
  );
  const gitStatus = retainedGitStatus;
  const displayBranch = resolveDisplayedThreadBranch({
    threadBranch: thread.branch,
    currentGitBranch: gitStatus?.branch ?? null,
  });
  return { gitStatus, displayBranch };
}

/** Change-request identity and status for the row, from forge's shared PR
 * row surface. */
function useRowPullRequests(
  props: SidebarV2RowProps,
  displayBranch: string | null,
  gitStatus: ReturnType<typeof useRowGit>["gitStatus"],
) {
  const { thread } = props;
  return useSidebarThreadPullRequest(thread, displayBranch, gitStatus, props.linkedPrUrl);
}

/** The six-state visual status, the unread and wake signals, and the recede
 * decision — the v2 status model over the lease-gated visit record. */
function useRowStatus(props: SidebarV2RowProps, threadKey: string) {
  const lastVisitedAt = useUiStateStore((state) => state.threadLastVisitedAtById[threadKey]);
  const isSelected = useThreadSelectionStore((state) => state.selectedThreadKeys.has(threadKey));
  const isUnread = hasUnseenCompletion({
    latestTurn: props.thread.latestTurn,
    lastVisitedAt,
  });
  const status = resolveSidebarThreadStatus(props.thread);
  const lastVisitedMs = lastVisitedAt === undefined ? null : Date.parse(lastVisitedAt);
  const wokeAtMs = props.wokeAt === null ? null : Date.parse(props.wokeAt);
  const isWoke =
    wokeAtMs !== null &&
    Number.isFinite(wokeAtMs) &&
    (lastVisitedMs === null || Number.isNaN(lastVisitedMs) || lastVisitedMs < wokeAtMs) &&
    props.thread.settledOverride !== "settled";
  const shouldRecede = shouldRecedeSidebarThread({
    status,
    isUnread,
    isWoke,
    isActive: props.isActive,
    isSelected,
  });
  return { lastVisitedAt, isSelected, isUnread, status, isWoke, shouldRecede };
}

/** The status line's leading entry: the strongest signal on the row, with
 * the wake case promoted to an acknowledged action. */
function useRowTopStatus(
  status: ReturnType<typeof resolveSidebarThreadStatus>,
  isUnread: boolean,
  isWoke: boolean,
): {
  readonly label: string;
  readonly icon: "working" | "monitoring" | "approval" | "input" | "failed" | "woke" | "done";
  readonly className: string;
} | null {
  if (status === "working") {
    return { label: "Working", icon: "working", className: "text-hue-sky-foreground" };
  }
  if (status === "monitoring") {
    return { label: "Monitoring", icon: "monitoring", className: "text-foreground" };
  }
  if (status === "approval") {
    return { label: "Approval", icon: "approval", className: "text-warning-foreground" };
  }
  if (status === "input") {
    return { label: "Input", icon: "input", className: "text-hue-indigo-foreground" };
  }
  if (status === "failed") {
    return { label: "Failed", icon: "failed", className: "text-destructive-foreground" };
  }
  if (isWoke) {
    return { label: "Woke", icon: "woke", className: "text-warning-foreground" };
  }
  if (isUnread) {
    return { label: "Done", icon: "done", className: "text-success-foreground" };
  }
  return null;
}

/** Row-level interaction handlers, split from the subscriptions above so
 * neither side grows under the other's complexity weight. */
function useRowHandlers(props: SidebarV2RowProps, threadRef: ScopedThreadRef) {
  const {
    isRenaming,
    onCancelRename,
    onCommitRename,
    onContextMenu,
    onAcknowledgeWoke,
    onSettle,
    onStartRename,
    onThreadActivate,
    onThreadClick,
    onUnsettle,
    onUnsnooze,
    onUnpin,
    renamingTitle,
    thread,
  } = props;
  const [renameCommitted, setRenameCommitted] = useState(false);
  useEffect(() => {
    if (isRenaming) setRenameCommitted(false);
  }, [isRenaming]);
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      onThreadClick(event, threadRef);
    },
    [onThreadClick, threadRef],
  );
  const handleAcknowledgeWokeClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (props.wokeAt === null) return;
      onAcknowledgeWoke(threadRef, props.wokeAt);
    },
    [onAcknowledgeWoke, props.wokeAt, threadRef],
  );
  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      onContextMenu(threadRef, { x: event.clientX, y: event.clientY });
    },
    [onContextMenu, threadRef],
  );
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.target !== event.currentTarget) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onThreadActivate(threadRef);
    },
    [onThreadActivate, threadRef],
  );
  const handleDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      if (isRenaming || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if ((event.target as HTMLElement).closest("button, a, input")) return;
      event.preventDefault();
      onStartRename(threadRef, thread.title);
    },
    [isRenaming, onStartRename, thread.title, threadRef],
  );
  const handleRenameKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (event.nativeEvent.isComposing) return;
      if (event.key === "Enter") {
        event.preventDefault();
        setRenameCommitted(true);
        onCommitRename(threadRef, renamingTitle, thread.title);
      } else if (event.key === "Escape") {
        event.preventDefault();
        setRenameCommitted(true);
        onCancelRename();
      }
    },
    [onCancelRename, onCommitRename, renamingTitle, thread.title, threadRef],
  );
  const handleRenameBlur = useCallback(() => {
    if (!renameCommitted) {
      onCommitRename(threadRef, renamingTitle, thread.title);
    }
  }, [onCommitRename, renameCommitted, renamingTitle, thread.title, threadRef]);
  const settle = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onSettle(threadRef);
    },
    [onSettle, threadRef],
  );
  const unsettle = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onUnsettle(threadRef);
    },
    [onUnsettle, threadRef],
  );
  const unsnooze = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onUnsnooze(threadRef);
    },
    [onUnsnooze, threadRef],
  );
  const unpin = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onUnpin(threadRef);
    },
    [onUnpin, threadRef],
  );
  return {
    handleClick,
    handleAcknowledgeWokeClick,
    handleContextMenu,
    handleKeyDown,
    handleDoubleClick,
    handleRenameKeyDown,
    handleRenameBlur,
    settle,
    unsettle,
    unsnooze,
    unpin,
  };
}

export interface SidebarV2RowModel {
  readonly threadRef: ScopedThreadRef;
  readonly threadKey: string;
  readonly rowRef: ReturnType<typeof useRowLease>["rowRef"];
  readonly isSelected: boolean;
  readonly terminalStatus: ReturnType<typeof terminalStatusFromRunningIds>;
  readonly terminalProcessCount: number;
  readonly displayBranch: string | null;
  readonly pr: ReturnType<typeof useRowPullRequests>["pr"];
  readonly linkedPrIndicator: ReturnType<typeof useRowPullRequests>["indicator"];
  readonly prEntries: ReturnType<typeof useRowPullRequests>["entries"];
  readonly status: ReturnType<typeof resolveSidebarThreadStatus>;
  readonly isWoke: boolean;
  readonly shouldRecede: boolean;
  readonly topStatus: ReturnType<typeof useRowTopStatus>;
  readonly isWokeStatus: boolean;
  readonly snoozeAvailable: boolean;
  readonly snoozeMenuOpen: boolean;
  readonly setSnoozeMenuOpen: (open: boolean) => void;
  readonly handleClick: (event: React.MouseEvent) => void;
  readonly handleAcknowledgeWokeClick: (event: React.MouseEvent) => void;
  readonly handleContextMenu: (event: React.MouseEvent) => void;
  readonly handleKeyDown: (event: React.KeyboardEvent) => void;
  readonly handleDoubleClick: (event: React.MouseEvent) => void;
  readonly handleRenameKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  readonly handleRenameBlur: () => void;
  readonly settle: (event: React.MouseEvent) => void;
  readonly unsettle: (event: React.MouseEvent) => void;
  readonly unsnooze: (event: React.MouseEvent) => void;
  readonly unpin: (event: React.MouseEvent) => void;
}

/** Compose the row model from the focused hooks above. */
export function useSidebarV2RowModel(props: SidebarV2RowProps): SidebarV2RowModel {
  const threadRef = scopeThreadRef(props.thread.environmentId, props.thread.id);
  const threadKey = scopedThreadKey(threadRef);
  const lease = useRowLease(props);
  const terminal = useRowTerminal(threadRef);
  const { gitStatus, displayBranch } = useRowGit(props, lease.leaseLiveStatus);
  const pullRequests = useRowPullRequests(props, displayBranch, gitStatus);
  const statusModel = useRowStatus(props, threadKey);
  const topStatus = useRowTopStatus(statusModel.status, statusModel.isUnread, statusModel.isWoke);
  const handlers = useRowHandlers(props, threadRef);

  const [snoozeMenuOpenRaw, setSnoozeMenuOpen] = useState(false);
  const snoozeAvailable = canSnooze(props.thread);
  const snoozeMenuOpen = snoozeMenuOpenRaw && snoozeAvailable;
  useEffect(() => {
    if (!snoozeAvailable) setSnoozeMenuOpen(false);
  }, [snoozeAvailable]);

  return {
    threadRef,
    threadKey,
    rowRef: lease.rowRef,
    isSelected: statusModel.isSelected,
    terminalStatus: terminal.terminalStatus,
    terminalProcessCount: terminal.terminalProcessCount,
    displayBranch,
    pr: pullRequests.pr,
    linkedPrIndicator: pullRequests.indicator,
    prEntries: pullRequests.entries,
    status: statusModel.status,
    isWoke: statusModel.isWoke,
    shouldRecede: statusModel.shouldRecede,
    topStatus,
    isWokeStatus: topStatus?.icon === "woke",
    snoozeAvailable,
    snoozeMenuOpen,
    setSnoozeMenuOpen,
    ...handlers,
  };
}
